import { Mail } from 'lucide-react';

const TermsAndConditionsPage = () => {
  const sections = [
    {
      title: 'Definitions',
      content: [
        'AgriMocks: Refers to the website, Services, content, and intellectual property owned or licensed by us.',
        'Services: Includes mock tests, previous year question paper and other educational resources for agriculture related exams.',
        'User: Any individual, entity, or representative accessing or using the Platform.'
      ]
    },
    {
      title: 'Eligibility',
      content: [
        'You must be at least 13 years old to use AgriMocks. Users under 18 require parental/guardian consent.',
        'By using the Platform, you confirm compliance with all applicable laws and these Terms.'
      ]
    },
    {
      title: 'Account Registration',
      content: [
        'To access paid Services, you must create an account with accurate and complete information.',
        'You are responsible for maintaining account confidentiality and all activities under your account.',
        'We reserve the right to suspend or terminate accounts for violations, fraud, or misuse.'
      ]
    },
    {
      title: 'Services',
      content: [
        'AgriMocks provides exam preparation resources for educational purposes only. We do not guarantee exam success.',
        'We may modify, suspend, or discontinue Services at any time without notice.'
      ]
    },
    {
      title: 'Payments, Refunds, and Cancellations',
      content: [
        'Fees: Access to premium Services requires payment of fees displayed on the Platform.',
        'Non-Refundable: Fees are non-refundable unless required by law or specified in a separate refund policy.',
        'Chargebacks: Unauthorized payment disputes may result in account termination and legal action.'
      ]
    },
    {
      title: 'Intellectual Property Rights',
      content: [
        'All content (text, graphics, logos, tests, etc.) is owned by AgriMocks or its licensors and protected under Indian copyright and trademark laws.',
        'Permitted Use: You may access content for personal, non-commercial use only.',
        'Prohibited: Copying, distributing, modifying, reverse-engineering, or creating derivative works without written permission.'
      ]
    },
    {
      title: 'User Responsibilities and Conduct',
      content: [
        'You agree not to:',
        '• Share accounts, passwords, or access with third parties.',
        '• Use automated tools (bots, scrapers) to extract data.',
        '• Upload viruses, spam, or harmful code.',
        '• Post unlawful, defamatory, or infringing content.',
        '• Misrepresent your identity or affiliation.',
        'Consequences: Violations may lead to content removal, account suspension, or legal action.'
      ]
    },
    {
      title: 'Limitation of Liability',
      content: [
        'AgriMocks is not liable for:',
        '• Indirect, incidental, or consequential damages (e.g., loss of data, profits).',
        '• Errors, inaccuracies, or interruptions in Services.',
        '• Third-party actions or content.',
        'The Platform is provided "as is" without warranties of accuracy, reliability, or fitness for a purpose.'
      ]
    },
    {
      title: 'Indemnification',
      content: [
        'You agree to indemnify AgriMocks, its directors, and employees against all claims, losses, or expenses (including legal fees) arising from:',
        '• Your misuse of the Platform.',
        '• Violation of these Terms or third-party rights.'
      ]
    },
    {
      title: 'Privacy Policy',
      content: [
        'Your use of AgriMocks is subject to our Privacy Policy, which explains how we collect, use, and protect your data.'
      ]
    },
    {
      title: 'Termination',
      content: [
        'We may terminate or restrict your access to the Platform at our discretion, with or without notice.',
        'Upon termination, your right to use the Services ceases immediately.'
      ]
    },
    {
      title: 'Modifications to Terms',
      content: [
        'We may update these Terms periodically. Continued use after changes constitutes acceptance. Check the "Last Updated" date for revisions.'
      ]
    },
    {
      title: 'Third-Party Links and Content',
      content: [
        'AgriMocks may include links to third-party websites. We are not responsible for their content, policies, or practices.'
      ]
    },
    {
      title: 'Force Majeure',
      content: [
        'We are not liable for delays or failures due to events beyond our control (e.g., natural disasters, government restrictions).'
      ]
    },
    {
      title: 'Severability',
      content: [
        'If any provision is deemed unenforceable, the remaining Terms remain valid.'
      ]
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-green-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Terms and Conditions</h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 rounded-[24px] p-8 shadow-lg mb-12">
            <p className="text-gray-600 dark:text-gray-300">
              Welcome to AgriMocks ("Platform," "we," "us," or "our"). These Terms and Conditions ("Terms") 
              govern your access to and use of our website [www.agrimocks.com] and related services ("Services"). 
              By accessing or using AgriMocks, you ("User," "you," or "your") agree to be bound by these Terms.
            </p>
          </div>

          {/* Terms Content */}
          <div className="space-y-12">
            {sections.map((section, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-[24px] p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {index + 1}. {section.title}
                </h2>
                <div className="space-y-4">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-gray-600 dark:text-gray-300">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}

            {/* Contact Section */}
            <div className="bg-white dark:bg-gray-800 rounded-[24px] p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                16. Contact Us
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                For questions or grievances:
              </p>
              <div className="flex items-center text-gray-600 dark:text-gray-300">
                <Mail className="h-5 w-5 mr-3 text-green-600 dark:text-green-400" />
                <span>agrimocks2025@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditionsPage; 